import { useEffect, useMemo, useState } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import api from '../../utils/api'

export default function WebsiteDownloadStatsTab() {
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(true)
  const [websiteStats, setWebsiteStats] = useState({
    summary: null,
    daily: []
  })

  const fetchData = async (targetDays = days) => {
    setLoading(true)
    try {
      const res = await api.get('/admin/website-funnel-stats', { params: { days: targetDays } })
      if (res.data.success) {
        const payload =
          res.data && res.data.data && typeof res.data.data === 'object' && !Array.isArray(res.data.data)
            ? res.data.data
            : res.data
        setWebsiteStats({
          summary: payload?.summary || null,
          daily: payload?.daily || []
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(days)
  }, [])

  const funnelCards = useMemo(() => {
    const summaryData = websiteStats.summary || {}
    const byOs = summaryData.downloadsByOs || {}
    return [
      { id: 'visits', label: '访问量（按IP+小时去重）', value: Number(summaryData.visits || 0) },
      { id: 'downloads', label: '总下载量', value: Number(summaryData.downloads || 0) },
      { id: 'windows', label: 'Windows 下载量', value: Number(byOs.windows || 0) },
      { id: 'macIntel', label: 'Mac Intel 下载量', value: Number(byOs['macos-intel'] || 0) },
      { id: 'macApple', label: 'Mac Apple 下载量', value: Number(byOs['macos-apple'] || 0) }
    ]
  }, [websiteStats.summary])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">官网统计</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            统计主站官网首页的访问量与下载量（下载按系统版本拆分），不做任何广告回传。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
          >
            <option value={7}>近 7 天</option>
            <option value={30}>近 30 天</option>
            <option value={90}>近 90 天</option>
          </select>
          <button
            type="button"
            onClick={() => fetchData(days)}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-dclaw-100 bg-dclaw-50/40 p-5 dark:border-dclaw-900/40 dark:bg-dclaw-950/20">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {funnelCards.map((item) => (
            <div
              key={item.id}
              className="rounded-xl border border-white/80 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="text-sm text-gray-500 dark:text-gray-400">{item.label}</div>
              <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">{item.value}</div>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">日期</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">访问量</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">总下载量</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Windows</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Mac Intel</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Mac Apple</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                    </td>
                  </tr>
                ) : websiteStats.daily.length > 0 ? (
                  websiteStats.daily.map((row) => (
                    <tr key={row.day} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-700 dark:text-gray-200">{row.day}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.visits || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.downloads || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.windows || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.macIntel || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.macApple || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无数据
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
